import Cookies from 'js-cookie';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  addCartLines,
  createCartWithLines,
  fetchCart as fetchCartApi,
  removeCartLines,
  updateCartLines,
} from '../util/api';
import { storableError } from '../util/errors';

export const CART_COOKIE_KEY = 'activityfox-cart';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const isUuid = value => typeof value === 'string' && UUID_RE.test(value);

const cookieOptions = () => ({
  sameSite: 'strict',
  secure: typeof window !== 'undefined' && window.location.protocol === 'https:',
  expires: 365,
});

const setStoredCartId = cartId => {
  if (typeof window === 'undefined' || !isUuid(cartId)) {
    return;
  }

  Cookies.set(CART_COOKIE_KEY, cartId, cookieOptions());
};

const clearStoredCartId = () => {
  if (typeof window === 'undefined') {
    return;
  }

  Cookies.remove(CART_COOKIE_KEY);
};

const getStoredCartId = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const cartId = Cookies.get(CART_COOKIE_KEY);
  if (!cartId) {
    return null;
  }

  if (!isUuid(cartId)) {
    clearStoredCartId();
    return null;
  }

  return cartId;
};

const normalizeCart = result => result?.data || result || null;

export const fetchCart = createAsyncThunk(
  'cart/fetch',
  async (_, { rejectWithValue }) => {
    const cartId = getStoredCartId();
    if (!cartId) {
      return null;
    }

    try {
      const result = await fetchCartApi(cartId);
      return normalizeCart(result);
    } catch (error) {
      if (error?.status === 400 || error?.status === 404) {
        clearStoredCartId();
        return null;
      }

      return rejectWithValue(storableError(error));
    }
  },
  {
    condition: (_, { getState }) => {
      return !getState()?.cart?.fetchInProgress;
    },
  }
);

export const addLineItems = createAsyncThunk(
  'cart/addLineItems',
  async (lines, { rejectWithValue }) => {
    try {
      const cartId = getStoredCartId();
      const result = cartId ? await addCartLines(cartId, lines) : await createCartWithLines(lines);
      const cart = normalizeCart(result);

      if (cart?.id) {
        setStoredCartId(cart.id);
      }

      return cart;
    } catch (error) {
      return rejectWithValue(storableError(error));
    }
  }
);

export const updateLineItems = createAsyncThunk(
  'cart/updateLineItems',
  async (lines, { rejectWithValue }) => {
    try {
      const cartId = getStoredCartId();
      if (!cartId) {
        return null;
      }

      const result = await updateCartLines(cartId, lines);
      return normalizeCart(result);
    } catch (error) {
      return rejectWithValue(storableError(error));
    }
  }
);

export const removeLineItems = createAsyncThunk(
  'cart/removeLineItems',
  async (lineIds, { rejectWithValue }) => {
    try {
      const cartId = getStoredCartId();
      if (!cartId) {
        return null;
      }

      const result = await removeCartLines(cartId, lineIds);
      return normalizeCart(result);
    } catch (error) {
      return rejectWithValue(storableError(error));
    }
  }
);

const initialState = {
  data: null,
  fetchInProgress: false,
  fetchError: null,
  hasLoaded: false,
  mutationInProgress: false,
  mutationError: null,
  pendingAction: null,
  isOpen: false,
};

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    openCart: state => {
      state.isOpen = true;
    },
    closeCart: state => {
      state.isOpen = false;
    },
    toggleCart: state => {
      state.isOpen = !state.isOpen;
    },
    clearCartState: state => {
      clearStoredCartId();
      return { ...initialState, hasLoaded: true };
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchCart.pending, state => {
        state.fetchInProgress = true;
        state.fetchError = null;
      })
      .addCase(fetchCart.fulfilled, (state, action) => {
        state.fetchInProgress = false;
        state.data = action.payload;
        state.hasLoaded = true;
      })
      .addCase(fetchCart.rejected, (state, action) => {
        state.fetchInProgress = false;
        state.fetchError = action.payload || action.error;
        state.hasLoaded = true;
      })

      .addCase(addLineItems.pending, state => {
        state.mutationInProgress = true;
        state.mutationError = null;
        state.pendingAction = 'add';
      })
      .addCase(addLineItems.fulfilled, (state, action) => {
        state.mutationInProgress = false;
        state.pendingAction = null;
        state.data = action.payload;
        state.hasLoaded = true;
      })
      .addCase(addLineItems.rejected, (state, action) => {
        state.mutationInProgress = false;
        state.pendingAction = null;
        state.mutationError = action.payload || action.error;
      })

      .addCase(updateLineItems.pending, state => {
        state.mutationInProgress = true;
        state.mutationError = null;
        state.pendingAction = 'update';
      })
      .addCase(updateLineItems.fulfilled, (state, action) => {
        state.mutationInProgress = false;
        state.pendingAction = null;
        state.data = action.payload;
        state.hasLoaded = true;
      })
      .addCase(updateLineItems.rejected, (state, action) => {
        state.mutationInProgress = false;
        state.pendingAction = null;
        state.mutationError = action.payload || action.error;
      })

      .addCase(removeLineItems.pending, state => {
        state.mutationInProgress = true;
        state.mutationError = null;
        state.pendingAction = 'remove';
      })
      .addCase(removeLineItems.fulfilled, (state, action) => {
        state.mutationInProgress = false;
        state.pendingAction = null;
        state.data = action.payload;
        state.hasLoaded = true;
      })
      .addCase(removeLineItems.rejected, (state, action) => {
        state.mutationInProgress = false;
        state.pendingAction = null;
        state.mutationError = action.payload || action.error;
      });
  },
});

export const { openCart, closeCart, toggleCart, clearCartState } = cartSlice.actions;

export const selectCart = state => state.cart.data;
export const selectCartIsLoading = state => state.cart.fetchInProgress;
export const selectCartError = state => state.cart.fetchError;
export const selectCartHasLoaded = state => state.cart.hasLoaded;
export const selectCartMutationInProgress = state => state.cart.mutationInProgress;
export const selectCartMutationError = state => state.cart.mutationError;
export const selectCartPendingAction = state => state.cart.pendingAction;
export const selectIsCartOpen = state => state.cart.isOpen;

export default cartSlice.reducer;
